import { ref, computed } from 'vue';

// Simple per-session seeded RNG so numbers stay stable while
// the page is open, but change on full reload.
const sessionSeed = Date.now();
let rngState = sessionSeed % 2147483647 || 1;

function seededRandom() {
    // Park-Miller LCG
    rngState = (rngState * 16807) % 2147483647;
    return (rngState - 1) / 2147483646;
}

export function randomInt(min, max) {
    const low = Math.ceil(min);
    const high = Math.floor(max);
    return Math.floor(seededRandom() * (high - low + 1)) + low;
}

// Unified colors for test result labels
export const TEST_RESULT_LABELS = [
    'BAD CELL REPLACE',
    'RECHARGE & RETEST',
    'CAUTION',
    'BAD & REPLACE',
    'GOOD & RECHARGE',
    'GOOD & PASS',
];

export const TEST_RESULT_COLOR_MAP = {
    'BAD CELL REPLACE': '#D91E18',
    'RECHARGE & RETEST': '#FFBA66',
    CAUTION: '#FFD600',
    'BAD & REPLACE': '#EB4D47',
    'GOOD & RECHARGE': '#FFA73F',
    'GOOD & PASS': '#2E8B57',
};

const getTestResultColorArray = () =>
    TEST_RESULT_LABELS.map(label => TEST_RESULT_COLOR_MAP[label]);

// Aggregation config for test result trend (GOOD / BAD / OTHER / TOTAL)
export const TEST_RESULT_TREND_SERIES_DEFS = [
    {
        key: 'GOOD',
        label: 'GOOD',
        color: '#2E8B57',
        sourceLabels: ['GOOD & RECHARGE', 'GOOD & PASS'],
        scale: 1.0,
    },
    {
        key: 'BAD',
        label: 'BAD',
        color: '#D91E18',
        sourceLabels: ['BAD CELL REPLACE', 'BAD & REPLACE'],
        scale: 1.8,
    },
    {
        key: 'OTHER',
        label: 'OTHER',
        color: '#FFBA66',
        sourceLabels: ['RECHARGE & RETEST', 'CAUTION'],
        scale: 0.4,
    },
    {
        key: 'TOTAL',
        label: 'TOTAL',
        color: '#34495E',
        isTotal: true,
        scale: 1.8,
    },
];

export function buildTestResultTrendChart(baseChart, sliceSizeRaw) {
    if (!baseChart || !baseChart.data) {
        return null;
    }

    const baseData = baseChart.data || {};
    const labels = Array.isArray(baseData.labels) ? baseData.labels : [];
    const datasets = Array.isArray(baseData.datasets) ? baseData.datasets : [];

    if (!labels.length || !datasets.length) {
        return null;
    }

    const sliceSize = Math.max(
        1,
        Math.min(
            typeof sliceSizeRaw === 'number' ? sliceSizeRaw : 0,
            labels.length
        )
    );
    const startIndex = Math.max(labels.length - sliceSize, 0);

    const slicedLabels = labels.slice(startIndex);

    const baseDefs = TEST_RESULT_TREND_SERIES_DEFS.filter(
        def => def.key !== 'TOTAL'
    );

    const valuesByKey = {};
    baseDefs.forEach(def => {
        valuesByKey[def.key] = [];
    });
    const totalValues = [];

    for (let i = startIndex; i < labels.length; i += 1) {
        const idx = i - startIndex;

        let totalForPoint = 0;

        baseDefs.forEach(def => {
            let rawSum = 0;

            datasets.forEach(ds => {
                if (def.sourceLabels && def.sourceLabels.includes(ds.label)) {
                    const v =
                        Array.isArray(ds.data) && i < ds.data.length
                            ? Number(ds.data[i]) || 0
                            : 0;
                    rawSum += v;
                }
            });

            const factor =
                typeof def.scale === 'number' && Number.isFinite(def.scale)
                    ? def.scale
                    : 1;
            const scaledRaw = rawSum * factor;
            const scaledInt = Math.round(scaledRaw);
            const safeValue = Number.isFinite(scaledInt)
                ? Math.max(0, scaledInt)
                : 0;

            valuesByKey[def.key][idx] = safeValue;
            totalForPoint += safeValue;
        });

        totalValues[idx] = totalForPoint;
    }

    const lineDatasets = [
        ...baseDefs.map(def => ({
            label: def.label,
            data: valuesByKey[def.key] || [],
            borderColor: def.color,
            backgroundColor: `${def.color}33`,
            fill: false,
            tension: 0.2,
            pointRadius: 2,
        })),
        (() => {
            const totalDef = TEST_RESULT_TREND_SERIES_DEFS.find(
                def => def.key === 'TOTAL'
            ) || {
                label: 'TOTAL',
                color: '#34495E',
            };
            return {
                label: totalDef.label,
                data: totalValues,
                borderColor: totalDef.color,
                backgroundColor: `${totalDef.color}33`,
                fill: false,
                tension: 0.2,
                pointRadius: 2,
            };
        })(),
    ];

    return {
        ...baseChart,
        type: 'line',
        data: {
            ...baseData,
            labels: slicedLabels,
            datasets: lineDatasets,
        },
    };
}

// Central registry for all demo dashboard charts
export const chartsRegistry = [
    // Version distribution
    {
        id: 'firmwareVersionCounts',
        title: 'Firmware Versions',
        subTitle: 'All firmware version counts',
        type: 'bar',
        groupId: 'versions',
        order: 1,
        visibleOnModels: 'modelsOnly', // every model only
        data: {
            labels: ['FW_V01.A', 'FW_V01.B', 'FW_V01.C'],
            datasets: [
                {
                    label: 'Devices',
                    data: [
                        randomInt(80, 160),
                        randomInt(200, 360),
                        randomInt(140, 260),
                    ],
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: true,
            aspectRatio: 2.5, // 數字越大越扁，越小越高
        },
    },
    {
        id: 'appVersionCounts',
        title: 'App Versions',
        subTitle: 'All app version counts',
        type: 'bar',
        groupId: 'versions',
        order: 2,
        visibleOnModels: 'btw350Only',
        data: {
            labels: [
                'BTW350_DHC_V01.A',
                'BTW350_DHC_V01.B',
                'BTW350_DHC_V01.C',
            ],
            datasets: [
                {
                    label: 'Devices',
                    data: [
                        randomInt(40, 100),
                        randomInt(100, 180),
                        randomInt(30, 90),
                    ],
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: true,
                },
            },
        },
    },

    // Upload volume over time
    {
        id: 'yesterdayUploads2h',
        title: 'Yesterday Tests',
        subTitle: 'Count of tests uploaded by 2-hour slots',
        type: 'bar',
        groupId: 'uploadVolume',
        order: 1,
        visibleOnModels: 'all', // total/every model
        data: {
            labels: [
                '00-02',
                '02-04',
                '04-06',
                '06-08',
                '08-10',
                '10-12',
                '12-14',
                '14-16',
                '16-18',
                '18-20',
                '20-22',
                '22-24',
            ],
            datasets: [
                {
                    label: 'Tests',
                    data: [
                        randomInt(2, 8),
                        randomInt(4, 10),
                        randomInt(6, 14),
                        randomInt(10, 24),
                        randomInt(20, 40),
                        randomInt(28, 52),
                        randomInt(32, 60),
                        randomInt(30, 56),
                        randomInt(24, 50),
                        randomInt(16, 38),
                        randomInt(10, 26),
                        randomInt(4, 16),
                    ],
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: true,
            aspectRatio: 3,
        },
    },
    {
        id: 'last12MonthsTests',
        title: 'Last 12 Months',
        subTitle: 'BatteryTest count',
        type: 'line',
        groupId: 'uploadVolume',
        order: 2,
        visibleOnModels: 'all', // total/every model
        data: {
            labels: [
                'Jan',
                'Feb',
                'Mar',
                'Apr',
                'May',
                'Jun',
                'Jul',
                'Aug',
                'Sep',
                'Oct',
                'Nov',
                'Dec',
            ],
            datasets: [
                {
                    label: 'Tests',
                    data: [
                        randomInt(700, 900),
                        randomInt(850, 1050),
                        randomInt(1000, 1200),
                        randomInt(1100, 1300),
                        randomInt(1200, 1400),
                        randomInt(1350, 1600),
                        randomInt(1450, 1700),
                        randomInt(1500, 1800),
                        randomInt(1450, 1750),
                        randomInt(1350, 1650),
                        randomInt(1250, 1500),
                        randomInt(1150, 1400),
                    ],
                    fill: false,
                    tension: 0.2,
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: true,
                },
            },
        },
    },

    // Active shops
    {
        id: 'top5ShopsYesterday',
        title: 'Active Shops (Yesterday)',
        subTitle: 'Top 5 by uploads',
        type: 'bar',
        groupId: 'activeShopsShort',
        order: 1,
        visibleOnModels: 'all', // total/every model
        data: {
            labels: ['Shop A', 'Shop B', 'Shop C', 'Shop D', 'Shop E'],
            datasets: [
                {
                    label: 'Uploads',
                    data: [120, 110, 95, 80, 70],
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: true,
            aspectRatio: 2.5,
        },
    },
    {
        id: 'top5Shops7Days',
        title: 'Active Shops (7 days)',
        subTitle: 'Top 5 by uploads',
        type: 'bar',
        groupId: 'activeShopsShort',
        order: 2,
        visibleOnModels: 'all', // total/every model
        data: {
            labels: ['Shop A', 'Shop B', 'Shop C', 'Shop D', 'Shop E'],
            datasets: [
                {
                    label: 'Uploads',
                    data: [600, 550, 500, 450, 420],
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: true,
            aspectRatio: 2.5,
        },
    },
    {
        id: 'top5ShopsMonth',
        title: 'Active Shops (Month)',
        subTitle: 'Top 5 by uploads (this month)',
        type: 'bar',
        groupId: 'activeShopsLong',
        order: 1,
        visibleOnModels: 'all', // total/every model
        data: {
            labels: ['Shop A', 'Shop B', 'Shop C', 'Shop D', 'Shop E'],
            datasets: [
                {
                    label: 'Uploads',
                    data: [
                        randomInt(800, 1100),
                        randomInt(750, 1000),
                        randomInt(700, 950),
                        randomInt(650, 900),
                        randomInt(600, 850),
                    ],
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: true,
            aspectRatio: 2.5,
        },
    },
    {
        id: 'top5ShopsQuarter',
        title: 'Active Shops (Quarter)',
        subTitle: 'Top 5 by uploads (this quarter)',
        type: 'bar',
        groupId: 'activeShopsLong',
        order: 2,
        visibleOnModels: 'all', // total/every model
        data: {
            labels: ['Shop A', 'Shop B', 'Shop C', 'Shop D', 'Shop E'],
            datasets: [
                {
                    label: 'Uploads',
                    data: [
                        randomInt(2000, 2600),
                        randomInt(1800, 2400),
                        randomInt(1700, 2300),
                        randomInt(1600, 2200),
                        randomInt(1500, 2100),
                    ],
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: true,
            aspectRatio: 2.5,
        },
    },
    {
        id: 'top5ShopsYear',
        title: 'Active Shops (Year)',
        subTitle: 'Top 5 by uploads (this year)',
        type: 'bar',
        groupId: 'activeShopsLong',
        order: 3,
        visibleOnModels: 'all', // total/every model
        data: {
            labels: ['Shop A', 'Shop B', 'Shop C', 'Shop D', 'Shop E'],
            datasets: [
                {
                    label: 'Uploads',
                    data: [
                        randomInt(7000, 9000),
                        randomInt(6500, 8500),
                        randomInt(6200, 8200),
                        randomInt(6000, 8000),
                        randomInt(5800, 7800),
                    ],
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: true,
            aspectRatio: 2.5,
        },
    },

    // Active users
    {
        id: 'top5UsersYesterday',
        title: 'Active Users (Yesterday)',
        subTitle: 'Top 5 by uploads',
        type: 'bar',
        groupId: 'activeUsersShort',
        order: 1,
        visibleOnModels: 'all', // total/every model
        data: {
            labels: ['User A', 'User B', 'User C', 'User D', 'User E'],
            datasets: [
                {
                    label: 'Uploads',
                    data: [
                        randomInt(40, 60),
                        randomInt(36, 54),
                        randomInt(32, 48),
                        randomInt(24, 40),
                        randomInt(20, 36),
                    ],
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
        },
    },
    {
        id: 'top5Users7Days',
        title: 'Active Users (7 days)',
        subTitle: 'Top 5 by uploads',
        type: 'bar',
        groupId: 'activeUsersShort',
        order: 2,
        visibleOnModels: 'all', // total/every model
        data: {
            labels: ['User A', 'User B', 'User C', 'User D', 'User E'],
            datasets: [
                {
                    label: 'Uploads',
                    data: [210, 200, 185, 170, 150],
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
        },
    },
    {
        id: 'top5UsersMonth',
        title: 'Active Users (Month)',
        subTitle: 'Top 5 by uploads (this month)',
        type: 'bar',
        groupId: 'activeUsersLong',
        order: 1,
        visibleOnModels: 'all', // total/every model
        data: {
            labels: ['User A', 'User B', 'User C', 'User D', 'User E'],
            datasets: [
                {
                    label: 'Uploads',
                    data: [
                        randomInt(260, 360),
                        randomInt(240, 340),
                        randomInt(220, 320),
                        randomInt(200, 300),
                        randomInt(180, 280),
                    ],
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
        },
    },
    {
        id: 'top5UsersQuarter',
        title: 'Active Users (Quarter)',
        subTitle: 'Top 5 by uploads (this quarter)',
        type: 'bar',
        groupId: 'activeUsersLong',
        order: 2,
        visibleOnModels: 'all', // total/every model
        data: {
            labels: ['User A', 'User B', 'User C', 'User D', 'User E'],
            datasets: [
                {
                    label: 'Uploads',
                    data: [
                        randomInt(700, 1000),
                        randomInt(650, 950),
                        randomInt(620, 900),
                        randomInt(600, 880),
                        randomInt(580, 860),
                    ],
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
        },
    },
    {
        id: 'top5UsersYear',
        title: 'Active Users (Year)',
        subTitle: 'Top 5 by uploads (this year)',
        type: 'bar',
        groupId: 'activeUsersLong',
        order: 3,
        visibleOnModels: 'all', // total/every model
        data: {
            labels: ['User A', 'User B', 'User C', 'User D', 'User E'],
            datasets: [
                {
                    label: 'Uploads',
                    data: [
                        randomInt(2500, 3400),
                        randomInt(2300, 3200),
                        randomInt(2200, 3100),
                        randomInt(2100, 3000),
                        randomInt(2000, 2900),
                    ],
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
        },
    },

    // User growth
    {
        id: 'total_user_monthly',
        title: 'User Growth',
        subTitle: 'New vs total users by month',
        type: 'line',
        groupId: 'usersGrowth',
        order: 1,
        visibleOnModels: 'totalOnly', // total only
        data: {
            labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
            datasets: [
                {
                    label: 'New Users',
                    type: 'bar',
                    data: [20, 30, 40, 50, 60, 70],
                    backgroundColor: 'rgba(54, 162, 235, 0.6)',
                    borderColor: 'rgba(54, 162, 235, 1)',
                    borderWidth: 1,
                },
                {
                    label: 'Total Users',
                    type: 'line',
                    data: [100, 130, 170, 220, 280, 350],
                    borderColor: 'rgba(75, 192, 192, 1)',
                    backgroundColor: 'rgba(75, 192, 192, 0.3)',
                    borderWidth: 2,
                    pointBackgroundColor: 'rgba(75, 192, 192, 1)',
                    pointRadius: 3,
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
        },
    },

    // Test Result counts & ratios
    {
        id: 'testResultMonthly',
        title: 'Test Result by Month',
        subTitle: 'Result counts per month',
        type: 'bar',
        groupId: 'testResultTime',
        order: 1,
        visibleOnModels: 'modelsOnly', // every model only
        data: {
            // 24 months (two years) so we can slice
            labels: [
                'Jan 2024',
                'Feb 2024',
                'Mar 2024',
                'Apr 2024',
                'May 2024',
                'Jun 2024',
                'Jul 2024',
                'Aug 2024',
                'Sep 2024',
                'Oct 2024',
                'Nov 2024',
                'Dec 2024',
                'Jan 2025',
                'Feb 2025',
                'Mar 2025',
                'Apr 2025',
                'May 2025',
                'Jun 2025',
                'Jul 2025',
                'Aug 2025',
                'Sep 2025',
                'Oct 2025',
                'Nov 2025',
                'Dec 2025',
            ],
            datasets: [
                {
                    label: 'BAD CELL REPLACE',
                    data: Array.from({ length: 24 }, () => randomInt(18, 32)),
                    backgroundColor: TEST_RESULT_COLOR_MAP['BAD CELL REPLACE'],
                    borderColor: TEST_RESULT_COLOR_MAP['BAD CELL REPLACE'],
                },
                {
                    label: 'RECHARGE & RETEST',
                    data: Array.from({ length: 24 }, () => randomInt(24, 40)),
                    backgroundColor: TEST_RESULT_COLOR_MAP['RECHARGE & RETEST'],
                    borderColor: TEST_RESULT_COLOR_MAP['RECHARGE & RETEST'],
                },
                {
                    label: 'CAUTION',
                    data: Array.from({ length: 24 }, () => randomInt(30, 48)),
                    backgroundColor: TEST_RESULT_COLOR_MAP['CAUTION'],
                    borderColor: TEST_RESULT_COLOR_MAP['CAUTION'],
                },
                {
                    label: 'BAD & REPLACE',
                    data: Array.from({ length: 24 }, () => randomInt(40, 64)),
                    backgroundColor: TEST_RESULT_COLOR_MAP['BAD & REPLACE'],
                    borderColor: TEST_RESULT_COLOR_MAP['BAD & REPLACE'],
                },
                {
                    label: 'GOOD & RECHARGE',
                    data: Array.from({ length: 24 }, () => randomInt(70, 95)),
                    backgroundColor: TEST_RESULT_COLOR_MAP['GOOD & RECHARGE'],
                    borderColor: TEST_RESULT_COLOR_MAP['GOOD & RECHARGE'],
                },
                {
                    label: 'GOOD & PASS',
                    data: Array.from({ length: 24 }, () => randomInt(360, 440)),
                    backgroundColor: TEST_RESULT_COLOR_MAP['GOOD & PASS'],
                    borderColor: TEST_RESULT_COLOR_MAP['GOOD & PASS'],
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
        },
    },
    {
        id: 'testResultQuarterly',
        title: 'Test Result by Quarter',
        subTitle: 'Result counts per quarter',
        type: 'bar',
        groupId: 'testResultTime',
        order: 2,
        visibleOnModels: 'modelsOnly', // every model only
        data: {
            // 8 quarters (two years) so we can slice
            labels: [
                '2024 Q1',
                '2024 Q2',
                '2024 Q3',
                '2024 Q4',
                '2025 Q1',
                '2025 Q2',
                '2025 Q3',
                '2025 Q4',
            ],
            datasets: [
                {
                    label: 'BAD CELL REPLACE',
                    data: Array.from({ length: 8 }, () => randomInt(60, 90)),
                    backgroundColor: TEST_RESULT_COLOR_MAP['BAD CELL REPLACE'],
                    borderColor: TEST_RESULT_COLOR_MAP['BAD CELL REPLACE'],
                },
                {
                    label: 'RECHARGE & RETEST',
                    data: Array.from({ length: 8 }, () => randomInt(80, 120)),
                    backgroundColor: TEST_RESULT_COLOR_MAP['RECHARGE & RETEST'],
                    borderColor: TEST_RESULT_COLOR_MAP['RECHARGE & RETEST'],
                },
                {
                    label: 'CAUTION',
                    data: Array.from({ length: 8 }, () => randomInt(90, 140)),
                    backgroundColor: TEST_RESULT_COLOR_MAP['CAUTION'],
                    borderColor: TEST_RESULT_COLOR_MAP['CAUTION'],
                },
                {
                    label: 'BAD & REPLACE',
                    data: Array.from({ length: 8 }, () => randomInt(110, 160)),
                    backgroundColor: TEST_RESULT_COLOR_MAP['BAD & REPLACE'],
                    borderColor: TEST_RESULT_COLOR_MAP['BAD & REPLACE'],
                },
                {
                    label: 'GOOD & RECHARGE',
                    data: Array.from({ length: 8 }, () => randomInt(250, 270)),
                    backgroundColor: TEST_RESULT_COLOR_MAP['GOOD & RECHARGE'],
                    borderColor: TEST_RESULT_COLOR_MAP['GOOD & RECHARGE'],
                },
                {
                    label: 'GOOD & PASS',
                    data: Array.from({ length: 8 }, () =>
                        randomInt(1200, 1400)
                    ),
                    backgroundColor: TEST_RESULT_COLOR_MAP['GOOD & PASS'],
                    borderColor: TEST_RESULT_COLOR_MAP['GOOD & PASS'],
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
        },
    },
    {
        id: 'testResultRatioCurrentMonth',
        title: 'Test Result Ratio (This Month)',
        subTitle: 'Share by result type',
        type: 'pie',
        groupId: 'testResultRatio',
        order: 1,
        visibleOnModels: 'modelsOnly', // every model only
        data: {
            labels: [
                'BAD CELL REPLACE',
                'RECHARGE & RETEST',
                'CAUTION',
                'BAD & REPLACE',
                'GOOD & RECHARGE',
                'GOOD & PASS',
            ],
            datasets: [
                {
                    // Deliberately not summing to 100 so value vs % differ
                    // Scaled up for nicer demo visuals
                    data: [55, 65, 45, 85, 110, 185],
                    backgroundColor: getTestResultColorArray(),
                    borderColor: getTestResultColorArray(),
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            cutout: '60%',
            plugins: {
                legend: {
                    display: true,
                },
                doughnutCenterText: {
                    text: ({ data }) => {
                        const ds = data.datasets && data.datasets[0];
                        if (!ds || !Array.isArray(ds.data)) {
                            return '';
                        }

                        const values = ds.data;
                        const total = values.reduce(
                            (sum, v) => sum + (Number(v) || 0),
                            0
                        );

                        const labels = data.labels || [];
                        const targetLabels = [
                            'BAD CELL REPLACE',
                            'BAD & REPLACE',
                            'CAUTION',
                        ];

                        const badCautionCount = labels.reduce(
                            (sum, label, idx) => {
                                if (targetLabels.includes(label)) {
                                    return sum + (Number(values[idx]) || 0);
                                }
                                return sum;
                            },
                            0
                        );

                        const badCautionRatio =
                            total > 0
                                ? ((badCautionCount / total) * 100).toFixed(1)
                                : '0.0';

                        return [
                            `Total: ${total}`,
                            `Replace: ${badCautionRatio}%`,
                        ];
                    },
                    color: '#2c3e50',
                    fontSize: 13,
                },
            },
        },
    },
    {
        id: 'testResultRatioTotal',
        title: 'Test Result Ratio (Total)',
        subTitle: 'Overall share by result type',
        type: 'pie',
        groupId: 'testResultRatio',
        order: 2,
        visibleOnModels: 'modelsOnly', // every model only
        data: {
            labels: [
                'BAD CELL REPLACE',
                'RECHARGE & RETEST',
                'CAUTION',
                'BAD & REPLACE',
                'GOOD & RECHARGE',
                'GOOD & PASS',
            ],
            datasets: [
                {
                    // Deliberately not summing to 100 so value vs % differ
                    // Scaled up to look like > 1 year of accumulated data
                    // Total = 1200 + 900 + 600 + 1800 + 1500 + 3000 = 9000
                    // Bad+Rep+Caution = 1200 + 600 + 1800 = 3600 (40.0%)
                    data: [1200, 900, 600, 1800, 1500, 3000],
                    backgroundColor: getTestResultColorArray(),
                    borderColor: getTestResultColorArray(),
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            cutout: '60%',
            plugins: {
                legend: {
                    display: true,
                },
                doughnutCenterText: {
                    text: ['Total: 9000', 'Replace: 40.0%'],
                    color: '#2c3e50',
                    fontSize: 13,
                },
            },
        },
    },

    // Test type & battery type
    {
        id: 'testTypeRatioQuarter',
        title: 'Test Type Ratio (Quarter)',
        subTitle: 'Share by test type',
        type: 'pie',
        groupId: 'testTypeBattery',
        order: 1,
        visibleOnModels: 'modelsOnly', // every model only
        data: {
            labels: ['BATTERY TEST', 'SYSTEM TEST', 'IN VEHICLE TEST'],
            datasets: [
                {
                    // Not summing to 100 on purpose, scaled up
                    data: [260, 155, 135],
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            cutout: '60%',
            plugins: {
                legend: {
                    display: true,
                },
                doughnutCenterText: {
                    text: 'TOTAL',
                    color: '#2c3e50',
                    fontSize: 13,
                },
            },
        },
    },
    {
        id: 'batteryTypeLastAndThisMonth',
        title: 'Battery Types (Last vs This Month)',
        subTitle: 'Counts by battery type',
        type: 'bar',
        groupId: 'testTypeBattery',
        order: 2,
        visibleOnModels: 'modelsOnly', // every model only
        data: {
            labels: [
                'AGM FLAT PLATE',
                'AGM SPIRAL',
                'VRLA/GEL',
                'PS AGM',
                'EFB',
                'FLOODED',
            ],
            datasets: [
                {
                    label: 'Last Month',
                    data: [120, 80, 40, 30, 90, 150],
                    backgroundColor: '#A0AEC0',
                    borderColor: '#A0AEC0',
                },
                {
                    label: 'This Month',
                    data: [135, 88, 46, 36, 95, 165],
                    backgroundColor: '#63B3ED',
                    borderColor: '#63B3ED',
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: true,
                    position: 'top',
                },
                tooltip: {
                    mode: 'index',
                    intersect: false,
                },
            },
        },
    },
    {
        id: 'batteryTypeRatioYear',
        title: 'Battery Type Ratio (Year)',
        subTitle: 'Share by battery type',
        type: 'pie',
        groupId: 'testTypeBattery',
        order: 3,
        visibleOnModels: 'modelsOnly', // every model only
        data: {
            labels: [
                'AGM FLAT PLATE',
                'AGM SPIRAL',
                'VRLA/GEL',
                'PS AGM',
                'EFB',
                'FLOODED',
            ],
            datasets: [
                {
                    // Not summing to 100 on purpose, scaled up
                    data: [125, 95, 55, 45, 100, 130],
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            cutout: '60%',
            plugins: {
                legend: {
                    display: true,
                },
            },
        },
    },

    // Result with / without SD
    {
        id: 'resultWithSD',
        title: 'Results with SD (Last 12 months)',
        subTitle: 'BatteryTest + Archive with SD by result',
        type: 'pie',
        groupId: 'sdDistribution',
        order: 1,
        visibleOnModels: 'btw350Only',
        data: {
            labels: [
                'BAD CELL REPLACE',
                'RECHARGE & RETEST',
                'CAUTION',
                'BAD & REPLACE',
                'GOOD & RECHARGE',
                'GOOD & PASS',
            ],
            datasets: [
                {
                    label: 'SD',
                    // Not summing to 100 on purpose, scaled up
                    data: [95, 65, 45, 55, 110, 175],
                    backgroundColor: getTestResultColorArray(),
                    borderColor: getTestResultColorArray(),
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            cutout: '60%',
            plugins: {
                legend: {
                    display: true,
                },
                doughnutCenterText: {
                    text: 'WITH SD',
                    color: '#2c3e50',
                    fontSize: 13,
                },
            },
        },
    },
    {
        id: 'resultWithoutSD',
        title: 'Results without SD (Last 12 months)',
        subTitle: 'BatteryTest + Archive without SD by result',
        type: 'pie',
        groupId: 'sdDistribution',
        order: 2,
        visibleOnModels: 'btw350Only',
        data: {
            labels: [
                'BAD CELL REPLACE',
                'RECHARGE & RETEST',
                'CAUTION',
                'BAD & REPLACE',
                'GOOD & RECHARGE',
                'GOOD & PASS',
            ],
            datasets: [
                {
                    label: 'Non SD',
                    // Not summing to 100 on purpose, scaled up
                    data: [120, 75, 35, 45, 100, 180],
                    backgroundColor: getTestResultColorArray(),
                    borderColor: getTestResultColorArray(),
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            cutout: '60%',
            plugins: {
                doughnutCenterText: {
                    text: 'WITHOUT SD',
                    color: '#2c3e50',
                    fontSize: 13,
                },
            },
        },
    },
];

// Factory to create helpers bound to a specific model tab ref and local charts ref
export function useDemoCharts(
    activeModelTabRef,
    chartsRef = ref(chartsRegistry)
) {
    const isTotalModelSelected = computed(
        () => activeModelTabRef.value === 'All models'
    );

    const getChartsByGroup = groupId => {
        const model = activeModelTabRef.value;

        return chartsRef.value
            .filter(chart => chart.groupId === groupId)
            .filter(chart => {
                switch (chart.visibleOnModels) {
                    case 'all':
                        // total + every model
                        return true;
                    case 'totalOnly':
                        return isTotalModelSelected.value;
                    case 'modelsOnly':
                        return !isTotalModelSelected.value;
                    case 'btw350Only':
                        return model === 'BTW350';
                    default:
                        return true;
                }
            })
            .sort((a, b) => a.order - b.order);
    };

    return {
        charts: chartsRef,
        isTotalModelSelected,
        getChartsByGroup,
    };
}
